import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { formatDate } from '../utils/formatDateTime';
import ConfirmationModal from '../components/ConfirmationModal';
import {
  UserGroupIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  ShieldCheckIcon,
  MapIcon,
  CheckIcon,
  XMarkIcon,
  EyeIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';
import api from '../utils/api';

const PERMISSION_GROUPS = {
  alerts: {
    label: 'Alerts',
    actions: ['view', 'acknowledge', 'resolve'],
  },
  incidents: {
    label: 'Incidents',
    actions: ['view', 'create', 'edit', 'close'],
  },
  cameras: {
    label: 'Cameras',
    actions: ['view', 'configure'],
  },
  reports: {
    label: 'Reports',
    actions: ['view', 'export'],
  },
  users: {
    label: 'Users',
    actions: ['view', 'create', 'edit', 'delete'],
  },
};

export default function UsersPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [users, setUsers] = useState([]);
  const [sites, setSites] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [editingPermissions, setEditingPermissions] = useState(false);
  const [editingSiteAccess, setEditingSiteAccess] = useState(false);
  const [permissions, setPermissions] = useState({});
  const [siteAccess, setSiteAccess] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });

  // Check authorization
  useEffect(() => {
    if (!['org_admin', 'super_admin'].includes(user?.role)) {
      toast.error('You do not have permission to manage users');
      navigate('/dashboard');
      return;
    }
    fetchUsers();
    fetchSites();
  }, [user, navigate]);

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(search && { search }),
        ...(roleFilter && { role: roleFilter }),
      };
      const response = await api.get('/users', { params });
      setUsers(response.data?.data || response.data || []);
      setPagination(response.data?.pagination || { page: 1, limit: 20, total: 0 });
    } catch (error) {
      toast.error('Failed to load users');
      console.error('Error loading users:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSites = async () => {
    try {
      const response = await api.get('/sites');
      setSites(response.data.data || []);
    } catch (error) {
      console.error('Error fetching sites:', error);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setPagination({ ...pagination, page: 1 });
      fetchUsers();
    }, 300);
    return () => clearTimeout(timer);
  }, [search, roleFilter]);

  const handleSelectUser = async (selectedUser) => {
    setSelectedUser(selectedUser);
    setPermissions(selectedUser.permissions || {});
    setSiteAccess(selectedUser.siteAccess || []);
    setEditingPermissions(false);
    setEditingSiteAccess(false);
  };

  const handleSavePermissions = async () => {
    try {
      await api.put(`/users/${selectedUser.id}/permissions`, { permissions });
      setUsers(users.map(u => u.id === selectedUser.id ? { ...u, permissions } : u));
      setEditingPermissions(false);
      toast.success('Permissions updated successfully');
    } catch (error) {
      toast.error('Failed to update permissions');
      console.error('Error updating permissions:', error);
    }
  };

  const handleSaveSiteAccess = async () => {
    try {
      await api.put(`/users/${selectedUser.id}/site-access`, { siteAccess });
      setUsers(users.map(u => u.id === selectedUser.id ? { ...u, siteAccess } : u));
      setEditingSiteAccess(false);
      toast.success('Site access updated successfully');
    } catch (error) {
      toast.error('Failed to update site access');
      console.error('Error updating site access:', error);
    }
  };

  const handleToggleSiteAccess = (siteId) => {
    setSiteAccess(
      siteAccess.includes(siteId)
        ? siteAccess.filter(id => id !== siteId)
        : [...siteAccess, siteId]
    );
  };

  const handleTogglePermission = (resource, action) => {
    setPermissions({
      ...permissions,
      [resource]: permissions[resource]?.includes(action)
        ? permissions[resource].filter(a => a !== action)
        : [...(permissions[resource] || []), action],
    });
  };

  const [deleteUserTarget, setDeleteUserTarget] = useState(null);

  const handleDeleteUser = (userId) => {
    const user = users.find(u => u.id === userId);
    setDeleteUserTarget(user || { id: userId });
  };

  const confirmDeleteUser = async () => {
    if (!deleteUserTarget) return;
    try {
      await api.delete(`/users/${deleteUserTarget.id}`);
      setUsers(users.filter(u => u.id !== deleteUserTarget.id));
      setSelectedUser(null);
      toast.success('User deleted successfully');
    } catch (error) {
      toast.error('Failed to delete user');
      console.error('Error deleting user:', error);
    }
    setDeleteUserTarget(null);
  };

  const handleDeactivateUser = async (userId, isActive) => {
    try {
      await api.put(`/users/${userId}/activate`, { isActive: !isActive });
      setUsers(users.map(u => u.id === userId ? { ...u, isActive: !isActive } : u));
      toast.success(`User ${isActive ? 'deactivated' : 'activated'} successfully`);
    } catch (error) {
      toast.error('Failed to update user status');
      console.error('Error updating user status:', error);
    }
  };

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <UserGroupIcon className="w-6 h-6" />
            Users
          </h1>
          <p className="page-description">Manage team members and their permissions</p>
        </div>
        <button
          onClick={() => navigate('/users/create')}
          className="btn btn-primary"
        >
          Create User
        </button>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Users List */}
        <div className="flex-1">
          <div className="glass-card p-6">
            {/* Filters */}
            <div className="flex flex-col md:flex-row gap-4 mb-6">
              <div className="flex-1 relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search users..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="input pl-10 w-full"
                />
              </div>
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="input-select w-full md:w-32"
              >
                <option value="">All Roles</option>
                <option value="viewer">Viewer</option>
                <option value="supervisor">Supervisor</option>
                <option value="safety_officer">Safety Officer</option>
                <option value="site_manager">Site Manager</option>
                <option value="org_admin">Org Admin</option>
              </select>
            </div>

            {/* Users Table */}
            {isLoading ? (
              <div className="text-center py-8 text-slate-500">Loading users...</div>
            ) : users.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                No users found. <button className="text-[var(--theme-600)] hover:underline"
                  onClick={() => navigate('/users/create')}
                >
                  Create one now
                </button>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 font-semibold text-slate-700">Name</th>
                        <th className="text-left py-3 px-4 font-semibold text-slate-700">Email</th>
                        <th className="text-left py-3 px-4 font-semibold text-slate-700">Role</th>
                        <th className="text-left py-3 px-4 font-semibold text-slate-700">Status</th>
                        <th className="text-right py-3 px-4 font-semibold text-slate-700">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((row) => (
                        <tr
                          key={row.id}
                          onClick={() => handleSelectUser(row)}
                          className={clsx(
                            'border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors',
                            selectedUser?.id === row.id && 'bg-[var(--theme-50)]'
                          )}
                        >
                          <td className="py-3 px-4">
                            <div>
                              <p className="font-medium text-slate-800">{row.firstName} {row.lastName}</p>
                              <p className="text-xs text-slate-500">{row.lastLogin ? formatDate(row.lastLogin) : 'Never'}</p>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-slate-600">{row.email}</td>
                          <td className="py-3 px-4">
                            <span className="inline-block px-3 py-1 text-xs font-semibold rounded-full bg-[var(--theme-100)] text-[var(--theme-700)] capitalize">
                              {row.role}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            {row.isActive ? (
                              <span className="text-xs font-semibold text-green-600 bg-green-50 px-3 py-1 rounded-full">
                                Active
                              </span>
                            ) : (
                              <span className="text-xs font-semibold text-red-600 bg-red-50 px-3 py-1 rounded-full">
                                Inactive
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-right">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSelectUser(row);
                              }}
                              className="icon-btn icon-btn-sm"
                              title="Edit"
                            >
                              <PencilIcon className="w-4 h-4" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteUser(row.id);
                              }}
                              className="icon-btn icon-btn-sm icon-btn-danger ml-1"
                              title="Delete"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {pagination.pages > 1 && (
                  <div className="flex items-center justify-between mt-6 pt-6 border-t border-slate-200">
                    <p className="text-sm text-slate-600">
                      Page {pagination.page} of {pagination.pages} ({pagination.total} total)
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                        disabled={pagination.page === 1}
                        className="btn btn-secondary btn-sm"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                        disabled={pagination.page === pagination.pages}
                        className="btn btn-secondary btn-sm"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* User Details & Permissions */}
        {selectedUser && (
          <div className="lg:w-96 flex-shrink-0 space-y-6">
            {/* User Info Card */}
            <div className="glass-card p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-slate-800">User Details</h2>
                <button
                  onClick={() => setSelectedUser(null)}
                  className="icon-btn"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase">Name</p>
                  <p className="text-sm font-medium text-slate-800">
                    {selectedUser.firstName} {selectedUser.lastName}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase">Email</p>
                  <p className="text-sm font-medium text-slate-800">{selectedUser.email}</p>
                </div>
                {selectedUser.phone && (
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase">Phone</p>
                    <p className="text-sm font-medium text-slate-800">{selectedUser.phone}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase">Role</p>
                  <p className="text-sm font-medium text-slate-800 capitalize">{selectedUser.role}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase">Status</p>
                  <p className={clsx(
                    'text-sm font-medium',
                    selectedUser.isActive ? 'text-green-600' : 'text-red-600'
                  )}>
                    {selectedUser.isActive ? 'Active' : 'Inactive'}
                  </p>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-slate-200 space-y-2">
                <button
                  onClick={() => handleDeactivateUser(selectedUser.id, selectedUser.isActive)}
                  className="btn btn-secondary w-full"
                >
                  {selectedUser.isActive ? 'Deactivate' : 'Activate'}
                </button>
                <button
                  onClick={() => handleDeleteUser(selectedUser.id)}
                  className="btn btn-danger w-full"
                >
                  Delete User
                </button>
              </div>
            </div>

            {/* Permissions Card */}
            <div className="glass-card p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                  <ShieldCheckIcon className="w-5 h-5" />
                  Permissions
                </h3>
                {!editingPermissions && (
                  <button
                    onClick={() => setEditingPermissions(true)}
                    className="icon-btn icon-btn-sm"
                  >
                    <PencilSquareIcon className="w-4 h-4" />
                  </button>
                )}
              </div>

              {editingPermissions ? (
                <div className="space-y-4">
                  {Object.entries(PERMISSION_GROUPS).map(([resource, group]) => (
                    <div key={resource} className="p-3 bg-slate-50 rounded-lg">
                      <p className="font-medium text-slate-700 mb-2">{group.label}</p>
                      <div className="space-y-2">
                        {group.actions.map((action) => (
                          <label key={action} className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={permissions[resource]?.includes(action) || false}
                              onChange={() => handleTogglePermission(resource, action)}
                              className="w-4 h-4 rounded accent-[var(--theme-600)]"
                            />
                            <span className="text-sm text-slate-600 capitalize">{action}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}

                  <div className="flex gap-2 pt-4">
                    <button
                      onClick={handleSavePermissions}
                      className="btn btn-primary flex-1 btn-sm"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setEditingPermissions(false)}
                      className="btn btn-secondary flex-1 btn-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  {Object.entries(PERMISSION_GROUPS).map(([resource, group]) => {
                    const userPerms = permissions[resource] || [];
                    return (
                      <div key={resource}>
                        <p className="font-medium text-slate-700 mb-1">{group.label}</p>
                        {userPerms.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {userPerms.map((action) => (
                              <span
                                key={action}
                                className="text-xs px-2 py-1 rounded-full bg-[var(--theme-100)] text-[var(--theme-700)] font-medium capitalize"
                              >
                                {action}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-slate-400">No permissions</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Site Access Card */}
            {sites.length > 0 && (
              <div className="glass-card p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                    <MapIcon className="w-5 h-5" />
                    Site Access
                  </h3>
                  {!editingSiteAccess && (
                    <button
                      onClick={() => setEditingSiteAccess(true)}
                      className="icon-btn icon-btn-sm"
                    >
                      <PencilSquareIcon className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {editingSiteAccess ? (
                  <div className="space-y-2 mb-4">
                    {sites.map((site) => (
                      <label
                        key={site.id}
                        className="flex items-center gap-2 p-2 rounded hover:bg-slate-50"
                      >
                        <input
                          type="checkbox"
                          checked={siteAccess.includes(site.id)}
                          onChange={() => handleToggleSiteAccess(site.id)}
                          className="w-4 h-4 rounded accent-[var(--theme-600)]"
                        />
                        <span className="text-sm text-slate-600">{site.name}</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <div>
                    {siteAccess.length === 0 ? (
                      <p className="text-sm text-slate-600">Access to all sites</p>
                    ) : (
                      <div className="space-y-1">
                        {sites
                          .filter((site) => siteAccess.includes(site.id))
                          .map((site) => (
                            <p key={site.id} className="text-sm text-slate-600">• {site.name}</p>
                          ))}
                      </div>
                    )}
                  </div>
                )}

                {editingSiteAccess && (
                  <div className="flex gap-2 pt-4">
                    <button
                      onClick={handleSaveSiteAccess}
                      className="btn btn-primary flex-1 btn-sm"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setEditingSiteAccess(false)}
                      className="btn btn-secondary flex-1 btn-sm"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={!!deleteUserTarget}
        onClose={() => setDeleteUserTarget(null)}
        onConfirm={confirmDeleteUser}
        title="Delete User"
        message={`Are you sure you want to delete ${deleteUserTarget?.firstName ? `"${deleteUserTarget.firstName} ${deleteUserTarget.lastName}"` : 'this user'}? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
      />
    </div>
  );
}
